import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Phone,
  Mail,
  User,
  Copy,
  Clock,
  BellOff,
  BellRing,
} from "lucide-react";
import {
  JobSiteFormDialog,
  type JobSiteFormValues,
} from "@/components/job-sites/JobSiteFormDialog";
import { JobSiteLocationMap } from "@/components/job-sites/JobSiteLocationMap";

export const Route = createFileRoute("/_app/job-sites/$jobSiteId")({
  component: JobSiteDetailPage,
});

type Site = {
  id: string;
  name: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  notes: string | null;
  is_active: boolean;
  project_id: string;
  expected_clock_in_time: string | null;
  expected_clock_out_time: string | null;
  clock_alert_grace_minutes: number | null;
  clock_alerts_enabled: boolean | null;
  projects: { id: string; code: string; name: string } | null;
};

function JobSiteDetailPage() {
  const { t } = useTranslation();
  const { jobSiteId } = Route.useParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("job_sites")
      .select(
        "id, name, address_line1, address_line2, city, state, postal_code, country, latitude, longitude, contact_name, contact_phone, contact_email, notes, is_active, project_id, expected_clock_in_time, expected_clock_out_time, clock_alert_grace_minutes, clock_alerts_enabled, show_on_map, projects(id, code, name)",
      )
      .eq("id", jobSiteId)
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    setSite(data as unknown as Site);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSiteId]);

  async function onDelete() {
    if (!site) return;
    const { error } = await supabase.from("job_sites").delete().eq("id", site.id);
    if (error) return toast.error(error.message);
    toast.success(t("jobSiteDetail.deleted"));
    navigate({ to: "/job-sites" });
  }

  if (loading) {
    return (
      <div className="py-10 text-center text-muted-foreground">{t("jobSiteDetail.loading")}</div>
    );
  }
  if (!site) {
    return (
      <div className="space-y-3 py-10 text-center">
        <p className="text-muted-foreground">{t("jobSiteDetail.notFound")}</p>
        <Button asChild variant="outline">
          <Link to="/job-sites">{t("jobSiteDetail.backToList")}</Link>
        </Button>
      </div>
    );
  }

  const fullAddress = [
    site.address_line1,
    site.address_line2,
    [site.city, site.state, site.postal_code].filter(Boolean).join(", "),
    site.country,
  ]
    .filter(Boolean)
    .join("\n");

  const hasCoords = site.latitude != null && site.longitude != null;
  const addressString = [site.address_line1, site.city, site.state, site.postal_code, site.country]
    .filter(Boolean)
    .join(", ");
  const coordsString = hasCoords ? `${site.latitude}, ${site.longitude}` : "";

  const initial: JobSiteFormValues = {
    id: site.id,
    project_id: site.project_id,
    name: site.name,
    address_line1: site.address_line1 ?? "",
    address_line2: site.address_line2 ?? "",
    city: site.city ?? "",
    state: site.state ?? "",
    postal_code: site.postal_code ?? "",
    country: site.country ?? "US",
    latitude: site.latitude != null ? String(site.latitude) : "",
    longitude: site.longitude != null ? String(site.longitude) : "",
    contact_name: site.contact_name ?? "",
    contact_phone: site.contact_phone ?? "",
    contact_email: site.contact_email ?? "",
    notes: site.notes ?? "",
    is_active: site.is_active,
    expected_clock_in_time:
      (site as { expected_clock_in_time?: string | null }).expected_clock_in_time ?? "",
    expected_clock_out_time:
      (site as { expected_clock_out_time?: string | null }).expected_clock_out_time ?? "",
    clock_alert_grace_minutes: String(
      (site as { clock_alert_grace_minutes?: number }).clock_alert_grace_minutes ?? 30,
    ),
    clock_alerts_enabled: (site as { clock_alerts_enabled?: boolean }).clock_alerts_enabled ?? true,
    show_on_map: (site as { show_on_map?: boolean }).show_on_map ?? true,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/job-sites">
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t("jobSiteDetail.back")}
            </Link>
          </Button>
          <div>
            {site.projects && (
              <Link
                to="/projects/$projectId"
                params={{ projectId: site.projects.id }}
                className="font-mono text-xs text-muted-foreground hover:underline"
              >
                {site.projects.code} — {site.projects.name}
              </Link>
            )}
            <h1 className="text-2xl font-semibold tracking-tight">{site.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={site.is_active ? "default" : "secondary"}>
            {site.is_active ? t("jobSiteDetail.active") : t("jobSiteDetail.inactive")}
          </Badge>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t("jobSiteDetail.edit")}
            </Button>
          )}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("jobSiteDetail.deleteTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("jobSiteDetail.deleteDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("jobSiteDetail.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete}>
                    {t("jobSiteDetail.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("jobSiteDetail.location")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {t("jobSiteDetail.address")}
              </p>
              <p className="whitespace-pre-line">{fullAddress || "—"}</p>
            </div>
            {(site.latitude != null || site.longitude != null) && (
              <div>
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("jobSiteDetail.coordinates")}
                </p>
                <p className="font-mono">
                  {site.latitude}, {site.longitude}
                </p>
              </div>
            )}
            <JobSiteLocationMap
              name={site.name}
              latitude={site.latitude}
              longitude={site.longitude}
              address={addressString}
            />
            <div className="flex flex-wrap gap-2">
              {addressString && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(addressString);
                    toast.success(t("jobSiteDetail.addressCopied"));
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("jobSiteDetail.copyAddress")}
                </Button>
              )}
              {coordsString && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(coordsString);
                    toast.success(t("jobSiteDetail.coordsCopied"));
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("jobSiteDetail.copyCoords")}
                </Button>
              )}
            </div>
            {site.notes && (
              <div>
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("jobSiteDetail.notes")}
                </p>
                <p className="whitespace-pre-wrap">{site.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("jobSiteDetail.contact")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {site.contact_name ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{site.contact_name}</span>
              </div>
            ) : (
              <p className="text-muted-foreground">{t("jobSiteDetail.noContact")}</p>
            )}
            {site.contact_phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a href={`tel:${site.contact_phone}`} className="hover:underline">
                  {site.contact_phone}
                </a>
              </div>
            )}
            {site.contact_email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${site.contact_email}`} className="hover:underline">
                  {site.contact_email}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              {t("jobSiteDetail.schedule")}
            </CardTitle>
            {site.expected_clock_in_time || site.expected_clock_out_time ? (
              <Badge
                variant={site.clock_alerts_enabled ? "default" : "secondary"}
                className="gap-1"
              >
                {site.clock_alerts_enabled ? (
                  <BellRing className="h-3 w-3" />
                ) : (
                  <BellOff className="h-3 w-3" />
                )}
                {site.clock_alerts_enabled
                  ? t("jobSiteDetail.clockAlertsEnabled")
                  : t("jobSiteDetail.clockAlertsDisabled")}
              </Badge>
            ) : null}
          </CardHeader>
          <CardContent className="text-sm">
            {site.expected_clock_in_time || site.expected_clock_out_time ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {t("jobSiteDetail.expectedClockIn")}
                  </p>
                  <p className="font-mono text-lg">
                    {site.expected_clock_in_time?.slice(0, 5) ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {t("jobSiteDetail.expectedClockOut")}
                  </p>
                  <p className="font-mono text-lg">
                    {site.expected_clock_out_time?.slice(0, 5) ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {t("jobSiteDetail.clockGrace")}
                  </p>
                  <p className="font-mono text-lg">
                    {t("jobSiteDetail.clockGraceMinutes", {
                      minutes: site.clock_alert_grace_minutes ?? 30,
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">{t("jobSiteDetail.scheduleNotSet")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <JobSiteFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={initial}
        projectId={site.project_id}
        onSaved={load}
      />
    </div>
  );
}
